import { accentFor, accentText, onAccent } from "@/lib/colour";

const lines = (t) => (t || "").split("\n").map((s) => s.replace(/^[-•*]\s*/, "").trim()).filter(Boolean);
const skillList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const contact = (d) => [d.email, d.phone, d.location, d.website].filter(Boolean);
const dates = (e) => [e.start, e.end].filter(Boolean).join(" – ");
const hasExp = (d) => (d.experience || []).length > 0;
const hasEdu = (d) => (d.education || []).length > 0;

const Bullets = ({ text, className = "" }) => (
  <ul className={`mt-1.5 space-y-1 ${className}`}>
    {lines(text).map((b, i) => (
      <li key={i} className="relative pl-4 before:absolute before:left-0 before:top-[0.6em] before:h-[4px] before:w-[4px] before:rounded-full before:bg-current">{b}</li>
    ))}
  </ul>
);

function Classic({ d }) {
  const H = ({ children }) => <h3 className="mb-2 mt-6 border-b border-[#0F172A] pb-1 text-[12.5px] font-semibold uppercase tracking-[0.22em]" style={{ fontFamily: "Spectral, Georgia, serif" }}>{children}</h3>;
  return (
    <div className="px-[64px] py-[56px] text-[13px] leading-[1.55] text-[#111827]" style={{ fontFamily: "Spectral, Georgia, serif" }}>
      <header className="text-center">
        <h1 className="text-[34px] font-medium leading-tight" style={{ fontFamily: "Spectral, Georgia, serif" }}>{d.name || "Your Name"}</h1>
        {d.title && <p className="mt-1 text-[12.5px] uppercase tracking-[0.25em] text-[#475569]">{d.title}</p>}
        <p className="mt-3 text-[12px] text-[#334155]">{contact(d).join("   ·   ")}</p>
      </header>
      {d.summary && <><H>Profile</H><p>{d.summary}</p></>}
      {hasExp(d) && <H>Experience</H>}
      {(d.experience || []).map((e) => (
        <div key={e.id} className="mb-3.5">
          <div className="flex justify-between gap-4"><strong className="font-semibold">{e.role}{e.company && `, ${e.company}`}</strong><span className="shrink-0 italic text-[#475569]">{dates(e)}</span></div>
          {e.location && <p className="italic text-[#475569]">{e.location}</p>}
          <Bullets text={e.bullets} />
        </div>
      ))}
      {hasEdu(d) && <H>Education</H>}
      {(d.education || []).map((e) => (
        <div key={e.id} className="mb-2">
          <div className="flex justify-between gap-4"><strong className="font-semibold">{e.degree}</strong><span className="shrink-0 italic text-[#475569]">{dates(e)}</span></div>
          <p>{e.school}</p>{e.details && <p className="text-[#475569]">{e.details}</p>}
        </div>
      ))}
      {skillList(d.skills).length > 0 && <><H>Skills</H><p>{skillList(d.skills).join("  ·  ")}</p></>}
    </div>
  );
}

function Modern({ d, accent }) {
  const H = ({ children }) => <h3 className="mb-2.5 mt-6 text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: accentText(accent), fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{children}</h3>;
  return (
    <div className="flex min-h-[1123px] text-[12.5px] leading-[1.55] text-[#0F172A]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
      <aside className="w-[34%] border-t-[6px] bg-[#F4F0E8] px-[30px] py-[48px]" style={{ borderColor: accent }}>
        <h1 className="text-[28px] font-bold leading-[1.1] tracking-tight" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{d.name || "Your Name"}</h1>
        {d.title && <p className="mt-2 text-[13px] font-medium text-[#475569]">{d.title}</p>}
        <H>Contact</H>
        <ul className="space-y-1 break-words text-[12px] text-[#334155]">{contact(d).map((c) => <li key={c}>{c}</li>)}</ul>
        {skillList(d.skills).length > 0 && <><H>Skills</H>
          <div className="flex flex-wrap gap-1.5">{skillList(d.skills).map((s) => <span key={s} className="rounded bg-white px-2 py-0.5 text-[11.5px]">{s}</span>)}</div></>}
        {hasEdu(d) && <H>Education</H>}
        {(d.education || []).map((e) => (
          <div key={e.id} className="mb-3"><p className="font-semibold">{e.degree}</p><p className="text-[#475569]">{e.school}</p><p className="text-[11.5px] text-[#8C8275]">{dates(e)}</p></div>
        ))}
      </aside>
      <main className="flex-1 border-t-[6px] border-[#0F172A] px-[36px] py-[42px]">
        {d.summary && <><H>Profile</H><p className="text-[13px]">{d.summary}</p></>}
        {hasExp(d) && <H>Experience</H>}
        {(d.experience || []).map((e) => (
          <div key={e.id} className="mb-4">
            <div className="flex items-baseline justify-between gap-3"><p className="text-[13.5px] font-bold">{e.role}</p><span className="shrink-0 text-[11.5px] text-[#8C8275]">{dates(e)}</span></div>
            <p className="font-medium text-[#475569]">{[e.company, e.location].filter(Boolean).join(" · ")}</p>
            <Bullets text={e.bullets} />
          </div>
        ))}
      </main>
    </div>
  );
}

function Minimal({ d }) {
  const mono = { fontFamily: '"JetBrains Mono", monospace' };
  const Row = ({ label, children }) => (
    <section className="grid grid-cols-[120px_1fr] gap-6 border-t border-[#E2DCD2] py-5">
      <h3 className="pt-0.5 text-[10.5px] uppercase tracking-[0.18em] text-[#8C8275]" style={mono}>{label}</h3><div>{children}</div>
    </section>
  );
  return (
    <div className="px-[60px] py-[60px] text-[12.5px] leading-[1.65] text-[#1F2937]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
      <header className="pb-6">
        <h1 className="text-[30px] font-semibold tracking-tight text-[#0F172A]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{d.name || "Your Name"}</h1>
        {d.title && <p className="text-[14px] text-[#475569]">{d.title}</p>}
        <p className="mt-3 text-[11px] text-[#475569]" style={mono}>{contact(d).join("  /  ")}</p>
      </header>
      {d.summary && <Row label="About"><p>{d.summary}</p></Row>}
      {hasExp(d) && <Row label="Experience">{(d.experience || []).map((e) => (
        <div key={e.id} className="mb-4 last:mb-0">
          <div className="flex justify-between gap-3"><p className="font-semibold text-[#0F172A]">{e.role}{e.company && <span className="font-normal text-[#475569]"> — {e.company}</span>}</p><span className="shrink-0 text-[10.5px] text-[#8C8275]" style={mono}>{dates(e)}</span></div>
          <Bullets text={e.bullets} />
        </div>))}</Row>}
      {hasEdu(d) && <Row label="Education">{(d.education || []).map((e) => (
        <div key={e.id} className="mb-2 flex justify-between gap-3"><p><span className="font-semibold text-[#0F172A]">{e.degree}</span>, {e.school}</p><span className="shrink-0 text-[10.5px] text-[#8C8275]" style={mono}>{dates(e)}</span></div>))}</Row>}
      {skillList(d.skills).length > 0 && <Row label="Skills"><p>{skillList(d.skills).join(", ")}</p></Row>}
    </div>
  );
}

function Bold({ d, accent }) {
  const ink = onAccent(accent), tx = accentText(accent);
  const H = ({ children }) => (
    <h3 className="mb-2.5 mt-6 flex items-center gap-2 text-[12px] font-extrabold uppercase tracking-[0.14em]" style={{ color: tx, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
      <span className="inline-block h-[10px] w-[10px]" style={{ background: accent }} />{children}
    </h3>
  );
  return (
    <div className="min-h-[1123px] text-[12.8px] leading-[1.55] text-[#0F172A]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
      <header className="px-[56px] pb-[30px] pt-[44px]" style={{ background: accent, color: ink }}>
        <h1 className="text-[36px] font-extrabold leading-[1.05] tracking-tight" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{d.name || "Your Name"}</h1>
        {d.title && <p className="mt-2 text-[15px] font-semibold opacity-85">{d.title}</p>}
        <p className="mt-4 text-[12px] opacity-90">{contact(d).join("   |   ")}</p>
      </header>
      <div className="px-[56px] pb-[48px] pt-[10px]">
        {d.summary && <><H>Profile</H><p className="text-[13.2px]">{d.summary}</p></>}
        {hasExp(d) && <H>Experience</H>}
        {(d.experience || []).map((e) => (
          <div key={e.id} className="mb-4 border-l-[3px] pl-4" style={{ borderColor: accent }}>
            <div className="flex items-baseline justify-between gap-3"><p className="text-[13.8px] font-bold">{e.role}</p><span className="shrink-0 text-[11.5px] font-semibold" style={{ color: tx }}>{dates(e)}</span></div>
            <p className="font-medium text-[#475569]">{[e.company, e.location].filter(Boolean).join(" · ")}</p>
            <Bullets text={e.bullets} />
          </div>
        ))}
        {hasEdu(d) && <H>Education</H>}
        {(d.education || []).map((e) => (
          <div key={e.id} className="mb-2.5 flex justify-between gap-3">
            <div><p className="font-bold">{e.degree}</p><p className="text-[#475569]">{e.school}{e.details && ` · ${e.details}`}</p></div>
            <span className="shrink-0 text-[11.5px] font-semibold" style={{ color: tx }}>{dates(e)}</span>
          </div>
        ))}
        {skillList(d.skills).length > 0 && <><H>Skills</H>
          <div className="flex flex-wrap gap-1.5">{skillList(d.skills).map((x) => <span key={x} className="rounded-sm px-2 py-0.5 text-[11.5px] font-semibold" style={{ background: accent, color: ink }}>{x}</span>)}</div></>}
      </div>
    </div>
  );
}

export const VIVID = ["#F25F4C", "#158577", "#F2A93B", "#6C4AB6"];
const VIVID_TEXT = ["#E04A37", "#158577", "#B7740A", "#6C4AB6"];
const pillInk = (i) => (i % 4 === 2 ? "#0F172A" : "#FFFFFF");
function Vivid({ d }) {
  const H = ({ children, i }) => {
    const c = VIVID_TEXT[i];
    return (
      <h3 className="mb-2.5 mt-7 text-[15px] font-extrabold" style={{ color: c, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
        <span className="mr-2 inline-block h-[12px] w-[12px] rounded-full align-[-1px]" style={{ background: c }} />{children}
      </h3>
    );
  };
  return (
    <div className="min-h-[1123px] text-[12.8px] leading-[1.6] text-[#1F2937]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
      <div className="flex h-[10px]">{VIVID.map((c) => <span key={c} className="flex-1" style={{ background: c }} />)}</div>
      <div className="px-[58px] py-[44px]">
        <header>
          <h1 className="text-[34px] font-extrabold leading-[1.1] tracking-tight text-[#0F172A]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{d.name || "Your Name"}</h1>
          {d.title && <p className="mt-1 text-[15px] font-semibold" style={{ color: VIVID_TEXT[3] }}>{d.title}</p>}
          <p className="mt-3 text-[12px] text-[#475569]">{contact(d).join("   ·   ")}</p>
        </header>
        {d.summary && <><H i={0}>About me</H><p className="text-[13.2px]">{d.summary}</p></>}
        {hasExp(d) && <H i={1}>Experience</H>}
        {(d.experience || []).map((e, i) => (
          <div key={e.id} className="mb-4">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-[13.8px] font-bold text-[#0F172A]">{e.role}{e.company && <span className="font-semibold" style={{ color: VIVID_TEXT[(i + 1) % 4] }}> @ {e.company}</span>}</p>
              <span className="shrink-0 text-[11.5px] text-[#8C8275]">{dates(e)}</span>
            </div>
            {e.location && <p className="text-[11.5px] text-[#8C8275]">{e.location}</p>}
            <Bullets text={e.bullets} />
          </div>
        ))}
        {hasEdu(d) && <H i={2}>Education</H>}
        {(d.education || []).map((e) => (
          <div key={e.id} className="mb-2.5 flex justify-between gap-3">
            <div><p className="font-bold text-[#0F172A]">{e.degree}</p><p className="text-[#475569]">{e.school}{e.details && ` · ${e.details}`}</p></div>
            <span className="shrink-0 text-[11.5px] text-[#8C8275]">{dates(e)}</span>
          </div>
        ))}
        {skillList(d.skills).length > 0 && <><H i={3}>Skills</H>
          <div className="flex flex-wrap gap-1.5">{skillList(d.skills).map((x, i) => <span key={x} className="rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold" style={{ background: VIVID[i % 4], color: pillInk(i) }}>{x}</span>)}</div></>}
      </div>
    </div>
  );
}

const MAP = { classic: Classic, modern: Modern, minimal: Minimal, bold: Bold, vivid: Vivid };

export const ResumeTemplate = ({ template = "classic", data, accent }) => {
  const T = MAP[template] || Classic;
  return <T d={data || {}} accent={accentFor(template, accent)} />;
};

export const LetterTemplate = ({ template = "classic", data, accent: picked }) => {
  const s = data?.sender || {};
  const accent = accentFor(template, picked);
  const font = template === "classic" ? "Spectral, Georgia, serif" : '"Plus Jakarta Sans", sans-serif';
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  return (
    <div className={`text-[13.5px] leading-[1.7] text-[#111827] ${template === "modern" ? "border-t-[6px]" : ""}`} style={{ fontFamily: font, borderColor: accent }}>
      {template === "vivid" && <div className="flex h-[10px]">{VIVID.map((c) => <span key={c} className="flex-1" style={{ background: c }} />)}</div>}
      {template === "bold" && (
        <header className="px-[72px] pb-[28px] pt-[40px]" style={{ background: accent, color: onAccent(accent) }}>
          <h1 className="text-[30px] font-extrabold" style={{ fontFamily: font }}>{s.name || "Your Name"}</h1>
          <p className="mt-1 text-[12px] opacity-90">{[s.email, s.phone, s.location].filter(Boolean).join("   |   ")}</p>
        </header>
      )}
      <div className="px-[72px] py-[60px]">
        {template !== "bold" && <header className={template === "classic" ? "text-center" : ""}>
          <h1 className={`text-[28px] ${template === "vivid" ? "font-extrabold" : "font-semibold"}`} style={{ fontFamily: font }}>{s.name || "Your Name"}</h1>
          <p className="mt-1 text-[12px] text-[#475569]" style={template === "minimal" ? { fontFamily: '"JetBrains Mono", monospace' } : {}}>
            {[s.email, s.phone, s.location].filter(Boolean).join("   ·   ")}
          </p>
        </header>}
        {template !== "bold" && <div className={`my-8 ${template === "minimal" || template === "vivid" ? "border-t border-[#E2DCD2]" : "border-t border-[#0F172A]/70"}`} />}
        <p className="text-[#475569]">{date}</p>
        {(data?.company || data?.role) && <p className="mt-1 font-semibold" style={template === "bold" ? { color: accentText(accent) } : template === "vivid" ? { color: VIVID[3] } : {}}>Re: {[data.role, data.company].filter(Boolean).join(" at ")}</p>}
        <div className="mt-6 space-y-4 whitespace-pre-wrap">
          {data?.content ? data.content.split(/\n\s*\n/).map((p, i) => <p key={i}>{p.trim()}</p>)
            : <p className="text-[#8C8275]">Your cover letter will appear here. Paste a job posting and let the AI write a first draft.</p>}
        </div>
      </div>
    </div>
  );
};
